/**
 * GFG-194 (Send Queue, GFG Admin) — unit U2. Frontend wire contract for the Send Queue tab: one
 * future send event per row (Bulk: 1 for the gift email + 1 per reminder; Automated: 1 per
 * projected occurrence), the list/filter endpoints, and the reschedule body. Mirrors the
 * sibling contract apps/gfgf-ui/src/interfaces/adminContacts.ts -- keep in sync with the backend
 * interfaces file for this domain when it lands. UI strings for this tab live in
 * constants/sendQueueCampaigns.ts, not here.
 */
import type { FilterOption } from "@/components/BlotterFilterBar";
import type { CampaignLinkStatus, SendQueueStatus } from "@/constants/campaings";
import { CAMPAIGN_STATUSES } from "@/constants/campaings";
import type { Stage3EmailState } from "@/interfaces/CampaignBuilder";

// Re-exported from the single status source of truth (`constants/campaings.ts`, `CAMPAIGN_STATUSES`).
export type { SendQueueStatus };

export type SendQueueKind = "gift" | "reminder" | "birthday" | "anniversary";
export type SendQueueCampaignType = "Bulk Send" | "Automated";
export type ReminderBasis = "after_send" | "before_expiry";
export type SendQueueWindow = "1" | "7" | "30" | "all";
export type SendQueueSortKey =
  | "scheduled_at"
  | "campaign"
  | "organization"
  | "recipients"
  | "status";

export const SEND_QUEUE_KINDS: SendQueueKind[] = ["gift", "reminder", "birthday", "anniversary"];
export const SEND_QUEUE_CAMPAIGN_TYPES: SendQueueCampaignType[] = ["Bulk Send", "Automated"];
export const SEND_QUEUE_STATUSES: SendQueueStatus[] = [
  CAMPAIGN_STATUSES.QUEUE_SENDING,
  CAMPAIGN_STATUSES.QUEUE_SCHEDULED,
  CAMPAIGN_STATUSES.QUEUE_PAUSED,
];
export const SEND_QUEUE_WINDOWS: SendQueueWindow[] = ["1", "7", "30", "all"];
export const SEND_QUEUE_SORT_KEYS: SendQueueSortKey[] = [
  "scheduled_at",
  "campaign",
  "organization",
  "recipients",
  "status",
];

/** One future send event. Bulk: 1 item for the gift email + 1 per reminder. Automated: 1 per projected occurrence. */
export interface SendQueueItem {
  id: number;
  campaignId: number;
  campaignTitle: string;
  campaignType: SendQueueCampaignType;
  organizationId: number | null;
  organization: string;
  /** `users.id` of the org's account lead; null → "Unassigned" filter */
  accountLeadId: number | null;
  accountLead: string | null;
  kind: SendQueueKind;
  /** "Gift email" | "Reminder 1 of 4" | "Birthday gifts" | "Anniversary gifts" — the cell recomposes "Reminder (i of N)" */
  sendLabel: string;
  reminderIndex: number | null; // 1-based
  reminderTotal: number | null; // N of "i of N"
  reminderBasis: ReminderBasis | null;
  recipients: number;
  scheduledAt: string; // ISO 8601 UTC
  expiry: string | null; // ISO 8601 UTC
  status: SendQueueStatus;
}

export interface SendQueueListParams {
  window?: SendQueueWindow; // default "7"
  search?: string; // campaign title OR organization name, ilike
  campaign_type?: SendQueueCampaignType[];
  send_type?: SendQueueKind[];
  status?: SendQueueStatus[];
  account_lead_id?: string[]; // ids as strings; "unassigned" = accountLeadId null
  sortBy?: SendQueueSortKey; // default "scheduled_at"
  sortDir?: "asc" | "desc"; // default "asc"
  page?: number;
  limit?: number;
}

export interface SendQueueListResult {
  success: boolean;
  data: SendQueueItem[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface SendQueueFilterOptions {
  accountLeads: FilterOption[]; // { value: "48", label: "Will Hoffmann" } + { value: "unassigned", label: "Unassigned" } when applicable
}

/** Same shape `components/campaignBuilder/design/EmailPreview.tsx` consumes. */
export type SendQueueEmailPreview = Stage3EmailState;

export interface SendQueueContact {
  id: number;
  name: string;
  email: string;
  status: CampaignLinkStatus;
  /**
   * ISO 8601 UTC instant the gift e-mail actually went out (`contacts.sent_at`), or null while it
   * has not — the modal's DELIVERED column, which renders an em dash for null. Deliberately NOT
   * coalesced to the campaign's scheduled date the way `adminContacts.delivered` is: the Send
   * Queue lists sends that have not happened, and a scheduled date under a Delivered column would
   * read as a delivery that never took place.
   */
  deliveredAt: string | null;
  /**
   * The recipient's redemption link — the modal's CAMPAIGN URL column — or null when they have
   * none. Built API-side by `buildContactGiftUrl`, so what an admin copies out of the table is
   * byte-identical to the link that was e-mailed.
   */
  campaignUrl: string | null;
}

/**
 * The PERSONALISED email preview for one recipient of a queued send (the contacts drawer's eye
 * icon) — the same branding/subject/body payload `SendQueueEmailPreview` returns, but with the
 * merge fields already resolved for this one recipient. Mirrors
 * apps/gfgf-api/src/interfaces/sendQueueCampaigns.ts::SendQueueContactEmailPreview field-for-field.
 */
export interface SendQueueContactEmailPreview extends SendQueueEmailPreview {
  contactId: number;
  recipientName: string;
  recipientEmail: string;
}

export interface SendQueueContactsResult {
  success: boolean;
  data: SendQueueContact[];
  total: number;
}

export interface RescheduleSendBody {
  scheduledAt: string; // ISO 8601; server: > now; reminder: < expiry
}

/** Request body for PATCH /admin/campaigns/send-queue/:id/contacts/:contactId — correcting a recipient's address. */
export interface UpdateSendContactEmailBody {
  email: string;
}
